import DatePicker from 'components/base/DatePicker';
import Movimientos from 'components/modules/project-management/dashboard/Movimientos';
import { Col, Row, ButtonGroup, Card, Form, Spinner, Button } from 'react-bootstrap';
import MovimientosDashboard from 'components/modules/project-management/dashboard/MovimientosDashboard';
import TiposDeMovimientosDashboard from 'components/modules/project-management/dashboard/TiposDeMovimientosDashboard';
import CheckButton from 'components/base/CheckButton';
import React, { ChangeEvent, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReactECharts from 'echarts-for-react';

// Importamos el componente de Cobros
import CobrosListView from 'pages/apps/project-management/CobrosListView';

// --- INTERFACES ORIGINALES ---
interface ProcRow {
  id: number;
  time: string;
  proc: string;
  caratula: string;
  descripcion: string;
  usuario: string;
}

interface ProcRows {
  procRows: ProcRow[];
}

interface TiposRow {
  id: string;
  nombre: string;
  descripcion: string;
  clase: string;
  titulo: string;
  texto: string;
  informe: string;
  proximo_paso: string;
  ppEnCuantosDias: number;
  estado: string;
}

interface DiasData {
  mtoCount: number;
  cap: number;
  ea: number;
  fam: number;
  geo: number;
  is: number;
  isv: number;
  la: number;
  msj: number;
  sag: number;
  smn: number;
  zcc: number;
  pv: number;
  giros: number;
}

export interface MetricaRow {
  fecha: string;
  nombre_metrica: string;
  cantidad_filas: number;
}

interface ChartSeries {
  name: string;
  type: string;
  smooth: boolean;
  symbol: string;
  symbolSize: number;
  data: number[];
}

// Iniciales de los miembros del estudio a graficar (Sin CAP ni GEO)
const MIEMBROS = ['EA', 'FAM', 'IS', 'ISV', 'LA', 'MSJ', 'SAG', 'SMN', 'ZCC'];

const ProjectManagement = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000';

  // --- ESTADOS ORIGINALES DEL TABLERO INFERIOR ---
  // Cálculo seguro de la fecha de hace 2 días usando el objeto Date nativo
  const getInitialDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 2);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return Number(`${yyyy}${mm}${dd}`);
  };

  const [fecha, setFecha] = useState(getInitialDate());
  const fechaString = fecha.toString();
  const formattedFecha = `${fechaString.slice(6, 8)}-${fechaString.slice(4, 6)}-${fechaString.slice(0, 4)}`;
  const [scaleView, setScaleView] = useState('week');
  const [diasData, setDiasData] = useState<DiasData | null>(null);
  const [procRows, setProcRows] = useState<ProcRows | null>(null);
  const [tiposRows, setTiposRows] = useState<TiposRow[]>([]);
  const [metricasData, setMetricasData] = useState<MetricaRow[]>([]);

  // --- ESTADOS DEL NUEVO GRÁFICO (SUPERIOR) ---
  const [selectedWeekOffset, setSelectedWeekOffset] = useState<number>(0);
  const [chartSeries, setChartSeries] = useState<ChartSeries[]>([]);
  const [xAxisData, setXAxisData] = useState<string[]>([]);
  const [loadingChart, setLoadingChart] = useState<boolean>(false);

  const weekOptions = Array.from({ length: 11 }, (_, i) => ({
    value: i,
    label: i === 0 ? 'Semana Actual' : `Hace ${i} semana${i > 1 ? 's' : ''}`
  }));

  // --- LÓGICA DE FECHAS PARA EL GRÁFICO (LUNES A VIERNES) ---
// --- LÓGICA DE FECHAS PARA EL GRÁFICO (LUNES A VIERNES) ---
  const getWeekDates = (offset: number) => {
    // 1. Tomamos la fecha actual (hoy)
    const today = new Date();
    // Reseteamos las horas para evitar problemas de zonas horarias al calcular días
    today.setHours(0, 0, 0, 0);

    // 2. Calculamos qué día de la semana es hoy (0 es Domingo, 1 es Lunes... 6 es Sábado)
    const dayOfWeek = today.getDay();
    
    // 3. Calculamos la diferencia en días para llegar al Lunes de ESTA semana.
    // Si hoy es Domingo (0), retrocedemos 6 días. Si es otro día, retrocedemos (dayOfWeek - 1)
    const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    
    // 4. Clonamos la fecha actual y aplicamos la diferencia para obtener el Lunes actual
    const currentMonday = new Date(today);
    currentMonday.setDate(today.getDate() - diffToMonday);

    // 5. Aplicamos el offset de semanas hacia atrás (offset * 7 días)
    const targetMonday = new Date(currentMonday);
    targetMonday.setDate(currentMonday.getDate() - (offset * 7));

    const dates = [];
    const labels = [];
    const diasSemana = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie'];

    // 6. Generamos los 5 días (Lunes a Viernes) a partir del Lunes objetivo
    for (let i = 0; i < 5; i++) {
      // Importante: Crear un nuevo objeto Date en cada iteración para no mutar el base
      const currentDate = new Date(targetMonday);
      currentDate.setDate(targetMonday.getDate() + i);
      
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      
      // Armamos el string YYYYMMDD exacto que necesita el backend (mtos/byDate/:fecha)
      dates.push(`${year}${month}${day}`);
      labels.push(`${diasSemana[i]} ${day}/${month}`);
    }

    return { dates, labels };
  };

  // --- EFFECT: DATOS DEL GRÁFICO ---
  useEffect(() => {
    const fetchWeeklyData = async () => {
      setLoadingChart(true);
      try {
        const { dates, labels } = getWeekDates(selectedWeekOffset);
        setXAxisData(labels);

        const promises = dates.map(date => axios.get(`${apiUrl}/mtos/byDate/${date}`));
        const responses = await Promise.all(promises);

        const seriesData = MIEMBROS.map(miembro => {
          const conteoPorDia = responses.map(res => {
            const filas = res.data?.procRows || [];
            return filas.filter((r: ProcRow) => r.usuario === miembro).length;
          });

          return {
            name: miembro,
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            data: conteoPorDia
          };
        });

        setChartSeries(seriesData);
      } catch (error) {
        console.error('Error fetching chart data:', error);
      } finally {
        setLoadingChart(false);
      }
    };

    fetchWeeklyData();
  }, [selectedWeekOffset, apiUrl]);

  // --- EFFECTS ORIGINALES DEL TABLERO INFERIOR ---
  const dateForPicker = new Date(
    parseInt(fechaString.slice(0, 4)),
    parseInt(fechaString.slice(4, 6)) - 1,
    parseInt(fechaString.slice(6, 8))
  );

  const handleDateChange = (selectedDates: Date[]) => {
    if (selectedDates.length > 0) {
      const selectedDate = selectedDates[0];
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const newFecha = Number(`${year}${month}${day}`);
      setFecha(newFecha);
    }
  };

  useEffect(() => {
    const fetchProcRows = async () => {
      try {
        const response = await axios.get(`${apiUrl}/mtos/byDate/${fecha}`);
        setProcRows(response.data);
      } catch (error) {
        console.error('Error fetching ProcRows data:', error);
      }
    };
    fetchProcRows();
  }, [fecha, apiUrl]);

  useEffect(() => {
    const fetchTiposRows = async () => {
      try {
        const response = await axios.get(`${apiUrl}/tMtos`);
        setTiposRows(response.data);
      } catch (error) {
        console.error('Error fetching ProcRows data:', error);
      }
    };
    fetchTiposRows();
  }, [fecha, apiUrl]);

  useEffect(() => {
    const fetchMetricas = async () => {
      try {
        const response = await axios.get(`${apiUrl}/metricas`);
        setMetricasData(response.data);
      } catch (error) {
        console.error('Error fetching metricas:', error);
      }
    };
    fetchMetricas();
  }, [apiUrl]);

  // Transformamos fetchDiasData en un useCallback para poder llamarlo en un useEffect
  const fetchDiasData = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/dias/id/${fecha}`);
      setDiasData(response.data);
    } catch (error) {
      console.error('Error fetching Dias data:', error);
      setDiasData({
        mtoCount: 0, cap: 0, ea: 0, fam: 0, geo: 0, is: 0, isv: 0, la: 0, msj: 0, sag: 0, smn: 0, zcc: 0, pv: 0, giros: 0,
      });
    }
  }, [fecha, apiUrl]);

  // Ejecutamos fetchDiasData automáticamente cada vez que cambia la fecha
  useEffect(() => {
    fetchDiasData();
  }, [fetchDiasData]);

  // Conservamos el botón para forzar una recarga manual
  function buttonPressed () {
    fetchDiasData();
  }

  // --- CONFIGURACIÓN ECHARTS ---
  const chartOptions = {
    tooltip: {
      trigger: 'axis',
      padding: [7, 10],
      backgroundColor: '#fff',
      borderColor: '#c7d0d9',
      borderWidth: 1,
      textStyle: { color: '#31374a' }
    },
    legend: {
      data: MIEMBROS,
      bottom: 0,
      icon: 'circle',
      itemWidth: 10,
      itemHeight: 10
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      top: '10%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: xAxisData,
      axisLine: { lineStyle: { color: '#cbd0dd' } },
      axisLabel: { color: '#525b75' }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: '#e3e6ed', type: 'dashed' } },
      axisLabel: { color: '#525b75' }
    },
    series: chartSeries
  };

  return (
    <>
      <div className="pb-5 mb-5">
        <Row className="g-4">
          {/* COLUMNA PRINCIPAL (GRÁFICO A TODO LO ANCHO) */}
          <Col xs={12}>
            <div className="mb-4">
              <h2 className="mb-2">Panel de Supervisión</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Resumen de productividad del estudio
              </h5>
            </div>
            
            <Card className="shadow-sm">
              <Card.Header className="d-flex align-items-center justify-content-between pb-0 border-0 pt-4 px-4">
                <h4 className="mb-0">Movimientos por Miembro</h4>
                <div style={{ width: '200px' }}>
                  <Form.Select 
                    size="sm" 
                    value={selectedWeekOffset} 
                    onChange={(e) => setSelectedWeekOffset(Number(e.target.value))}
                  >
                    {weekOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Form.Select>
                </div>
              </Card.Header>
              <Card.Body className="px-4 pb-4 pt-0">
                {loadingChart ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '350px' }}>
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : (
                  <ReactECharts
                    option={chartOptions}
                    style={{ height: '350px', width: '100%' }}
                  />
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* TABLERO INFERIOR ORIGINAL (DÍA ESPECÍFICO) */}
      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis pt-7 border-y">
        <Row className="gy-3 mb-6 justify-content-between">
          <Col md={9} xs="auto">
            <h2 className="mb-2 text-body-emphasis">Movimientos del {formattedFecha}</h2>
          </Col>
          <Col xs="auto" sm="auto" className="d-flex flex-column align-items-end">
            <ButtonGroup>
              <CheckButton
                label="Dia"
                id="day"
                inputProps={{
                  name: 'scaleView',
                  value: 'day',
                  checked: scaleView === 'day',
                  onChange: (e: ChangeEvent<HTMLInputElement>) => setScaleView(e.target.value)
                }}
              />
              <CheckButton
                label="Mes"
                id="month"
                inputProps={{
                  name: 'scaleView',
                  value: 'month',
                  checked: scaleView === 'month',
                  onChange: (e: ChangeEvent<HTMLInputElement>) => setScaleView(e.target.value)
                }}
              />
              <CheckButton
                label="Año"
                id="year"
                inputProps={{
                  name: 'scaleView',
                  value: 'year',
                  checked: scaleView === 'year',
                  onChange: (e: ChangeEvent<HTMLInputElement>) => setScaleView(e.target.value)
                }}
              />
            </ButtonGroup>
            <div className="mt-3">
              <DatePicker
                options={{
                  defaultDate: dateForPicker,
                  dateFormat: 'M j, Y'
                }}
                onChange={handleDateChange}
              />
            </div>
          </Col>
        </Row>
        
        <Row>
          <Col xs={12} xl={7} xxl={6}>
            <div className="mt-3 mb-4 d-flex justify-content-center">
              <Button onClick={buttonPressed} className="btn-sm">
                Analizar Dia
              </Button>
            </div>
            {diasData ? (
              <Movimientos
                mtos={diasData.mtoCount}
                cap={diasData.cap}
                ea={diasData.ea}
                fam={diasData.fam}
                geo={diasData.geo}
                is={diasData.is}
                isv={diasData.isv}
                lrac={diasData.la}
                msj={diasData.msj}
                sag={diasData.sag}
                smn={diasData.smn}
                zcc={diasData.zcc}
              />
            ) : (
              <p className="text-center text-muted">Cargando...</p>
            )}
          </Col>
        </Row>
      </div>      

      {/* TABLA DE MOVIMIENTOS DEL DÍA */}
      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis pt-6 border-top">
        <MovimientosDashboard procRows={procRows?.procRows || []} />
      </div>

      {/* COBROS LIST VIEW (REUBICADO) */}
      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis pt-6 border-top">
        <Row>
          <Col xs={12}>
            <Card className="shadow-sm">
              <Card.Body className="p-4">
                <CobrosListView />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis pt-6 pb-9 border-top">
        <TiposDeMovimientosDashboard tiposRows={tiposRows} />
      </div>
    </>
  );
};

export default ProjectManagement;